import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { GridColumn as Column, Grid, GridSortChangeEvent } from '@progress/kendo-react-grid'
import { DealDto } from 'api/Clients/Types/DealDto'
import { CreateFilter } from 'api/Identity/Types/CreateFilter'
import { PageData } from 'api/Pagination/Types'
import { Pager } from 'components/components/Pager'
import { GridLoading } from 'components/GridLoading'
import { Spinner } from 'components/Spinner'
import {
  createGridState,
  GridStateStatus,
  useGridFilter,
  useGridState,
  useGridStatusState,
} from 'features/DataGrid'
import { isGoBack } from 'lib/react-router-dom'
import { useAppDispatch, useAppSelector } from 'store'
import {
  GridFilterActionState,
  resetGridFilters,
  selectGridFilterByFilterId,
  selectGridFilters,
  setGridFilters,
} from 'store/grid'
import { pageableSettings } from 'store/grid/grid.slice'
import {
  fetchFilterAction,
  fetchFilterTypesAction,
  fetchTickmillCompaniesAction,
  removeFilterAction,
  saveFilterAction,
  updateFilterAction,
} from 'store/identity/identity.actions'
import { useApiClient } from 'utils/api/ApiClient'
import { IdentityApiClient } from 'utils/api/identityApi'
import { PartnershipsApiClient } from 'utils/api/partnershipsApi'
import { links } from 'utils/featureToggles'
import { isEmpty } from 'utils/is-empty'
import { TableWithExport } from 'utils/table/withExport'
import { TableStateContext, TableStateContextData } from 'utils/TableStateContext'

import { dealsColumns } from './columns'
import { DealsTableFilter, useDealsBuildQuery } from './DealsTableFilter'

const useMapStateToProps = () => ({
  filters: useAppSelector((state) => state.identity.filter),
  filterTypes: useAppSelector((state) => state.identity.filterType),
  gridState: useAppSelector((state) => state.grid),
  gridFilterByFilterIdSelector: useAppSelector(selectGridFilterByFilterId),
  hasFilters: useAppSelector(selectGridFilters).length > 0,
})

const useMapDispatchToProps = () => {
  const dispatch = useAppDispatch()

  const identityApiClient = useApiClient(IdentityApiClient)

  return {
    fetchFilterTypes: async () => dispatch(fetchFilterTypesAction(identityApiClient)),
    fetchFilters: async () => dispatch(fetchFilterAction(identityApiClient)),
    saveFilter: async (data: CreateFilter) => dispatch(saveFilterAction(data, identityApiClient)),
    updateFilter: async (filterId: string, data: CreateFilter) =>
      dispatch(updateFilterAction(filterId, data, identityApiClient)),
    removeFilter: async (id: string) => dispatch(removeFilterAction(id, identityApiClient)),
    setGridFilters: async (state: GridFilterActionState) => dispatch(setGridFilters(state)),
    resetGridFilters: async () => dispatch(resetGridFilters()),
    fetchTickmillCompanies: async () => dispatch(fetchTickmillCompaniesAction(identityApiClient)),
  }
}

interface DealsFetchProps {
  setDeals: Dispatch<SetStateAction<DealDto[]>>
  tableData: TableStateContextData
  setTableData: Dispatch<SetStateAction<TableStateContextData>>
}

const useDealsFetch = ({ setDeals, tableData, setTableData }: DealsFetchProps) => {
  const partnershipsApiClient = useApiClient(PartnershipsApiClient)
  const { gridState, gridActions } = useGridState()
  const { filters } = useMapStateToProps()
  const { fetchFilters } = useMapDispatchToProps()
  const { gridStatusState, gridStatusActions } = useGridStatusState()
  const { selectFilterForGoBackHistory } = useGridFilter(gridStatusActions)

  const { getQuery, getSort } = useDealsBuildQuery()

  useEffect(() => {
    ;(async () => {
      await fetchGrid(
        async (gridFilterState: GridFilterActionState): Promise<PageData<DealDto[]>> => {
          const searchQuery = getQuery(gridFilterState.filter)
          const sortQuery = getSort(gridFilterState.sort)
          const response = await partnershipsApiClient.getDeals({
            search: searchQuery,
            sort: sortQuery,
            pageIndex: gridFilterState.pageIndex,
            pageSize: gridFilterState.pageSize,
            skipCount: tableData?.skipCount !== false,
          })

          if (response?.items) {
            setDeals(response.items)
          }
          setTableData({ ...tableData, meta: response })

          if (isEmpty(filters)) await fetchFilters()

          return response
        }
      )
    })()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchGrid = async (
    callback: (gridFilterState: GridFilterActionState) => Promise<PageData<DealDto[]>>
  ) => {
    gridStatusActions.setLoading(true)

    const gridFilterState = selectFilterForGoBackHistory(
      createGridState({ skip: 0 }, gridState, { isGoBack })
    )

    const response = await callback(gridFilterState)
    if (response?.totalResults >= 0) {
      const total = response.totalResults

      gridActions.setGridFilters({
        total,
      })
    }

    gridStatusActions.setLoading(false)
  }

  return { isLoading: gridStatusState.isLoading, gridStatusState, gridStatusActions }
}

interface DealsPageableProps {
  setDeals: Dispatch<SetStateAction<DealDto[]>>
  tableData: TableStateContextData
  setTableData: Dispatch<SetStateAction<TableStateContextData>>
  gridStatusState: GridStateStatus
}

const useDealsPageable = ({
  setDeals,
  tableData,
  setTableData,
  gridStatusState,
}: DealsPageableProps) => {
  const partnershipsApiClient = useApiClient(PartnershipsApiClient)
  const { gridState, gridActions } = useGridState()
  const { getQuery, getSort } = useDealsBuildQuery()

  const handleSkipCountChange = async (skipCount: boolean) => {
    const searchQuery = getQuery()
    const sortQuery = getSort(gridState.sort)

    gridStatusState.gridStatusActions.setFetching(true)
    const response = await partnershipsApiClient.getDeals({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: gridState.pageIndex,
      pageSize: gridState.take,
      skipCount,
    })
    gridStatusState.gridStatusActions.setFetching(false)

    const total = response.totalResults

    if (response?.items) {
      setDeals(response.items)
    }
    gridActions.setGridFilters({
      total,
    })
    setTableData({ ...tableData, skipCount, meta: response })
  }

  const handleSortChange = async (event: GridSortChangeEvent) => {
    gridActions.setGridFilters({
      sort: event.sort,
    })

    const searchQuery = getQuery(gridState.filter)
    const sortQuery = getSort(event.sort)

    gridStatusState.gridStatusActions.setFetching(true)
    const response = await partnershipsApiClient.getDeals({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: gridState.pageIndex,
      pageSize: gridState.pageSize,
    })
    gridStatusState.gridStatusActions.setFetching(false)

    if (response?.items) {
      setDeals(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const handlePageChange = async (page: number) => {
    const searchQuery = getQuery()
    const sortQuery = getSort(gridState.sort)
    const pageIndex = page
    gridActions.setGridFilters({
      pageIndex,
    })

    gridStatusState.gridStatusActions.setFetching(true)
    const response = await partnershipsApiClient.getDeals({
      search: searchQuery,
      sort: sortQuery,
      pageIndex,
      pageSize: gridState.take,
      skipCount: tableData?.skipCount !== false,
    })
    gridStatusState.gridStatusActions.setFetching(false)

    const total = response.totalResults

    gridActions.setGridFilters({
      total,
    })
    if (response?.items) {
      setDeals(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const handlePageSizeChange = async (pageSize: number) => {
    await gridActions.setGridFilters({
      ...gridState,
      pageSize,
    })

    const searchQuery = getQuery()
    const sortQuery = getSort(gridState.sort)

    gridStatusState.gridStatusActions.setFetching(true)
    const response = await partnershipsApiClient.getDeals({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: 1,
      pageSize,
      skipCount: tableData?.skipCount !== false,
    })
    gridStatusState.gridStatusActions.setFetching(false)

    await gridActions.setGridFilters({
      ...gridState,
      pageIndex: 1,
      take: pageSize,
      total: response.totalResults,
    })
    if (response?.items) {
      setDeals(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  return { handleSortChange, handlePageChange, handlePageSizeChange, handleSkipCountChange }
}

const RecordDealsPage = (): JSX.Element => {
  const [tableData, setTableData] = useState<TableStateContextData>({ skipCount: true })
  const [deals, setDeals] = useState<DealDto[]>([])

  const { isLoading, ...gridStatusState } = useDealsFetch({
    setDeals,
    tableData,
    setTableData,
  })
  const { handleSortChange, handlePageChange, handlePageSizeChange, handleSkipCountChange } =
    useDealsPageable({
      setDeals,
      tableData,
      setTableData,
      gridStatusState,
    })
  const { gridState } = useGridState()

  const handleFilter = (data: DealDto[]) => {
    setDeals(data)
  }

  if (isLoading) {
    return <Spinner />
  }

  return (
    <>
      <Breadcrumbs crumbs={[{ title: 'Deals', link: links.brokerageDeals.url }]} />
      <DealsTableFilter
        tableData={tableData}
        setTableData={setTableData}
        gridStatusState={gridStatusState}
        onChange={handleFilter}
      />
      <div className='records-clients'>
        <GridLoading isLoading={gridStatusState.gridStatusState.isFetching}>
          <TableStateContext.Provider value={[tableData, setTableData]}>
            <TableWithExport data={deals} columns={dealsColumns}>
              <Grid
                resizable
                sortable
                data={deals}
                sort={gridState.sort}
                total={gridState.total}
                take={gridState.take}
                skip={gridState.skip}
                onSortChange={handleSortChange}
                pageable={pageableSettings}
                pager={() => (
                  <Pager
                    onSkipCountChange={handleSkipCountChange}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                    page={gridState.pageIndex}
                    pageSize={gridState.pageSize}
                    take={gridState.take}
                    skip={gridState.skip}
                  />
                )}
                style={{ zIndex: 0 }}
              >
                {dealsColumns
                  .filter((c) => c.show)
                  .map((column, idx) => (
                    <Column key={idx + 2} {...column} />
                  ))}
              </Grid>
            </TableWithExport>
          </TableStateContext.Provider>
        </GridLoading>
      </div>
    </>
  )
}

export default RecordDealsPage
